import { isResource, resolveId } from '@medplum/core'
import type {
  Appointment,
  Coding,
  Composition,
  CompositionSection,
  Encounter,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { groupBy } from 'lodash-es'
import { codeableConceptHasSystem } from '#src/codeableConcept.ts'
import {
  COMPOSITION_NOTE_AI_SUMMARY_FAILED_ATTEMPTS_SYSTEM,
  COMPOSITION_NOTE_AI_SUMMARY_GENERATED_AT_SYSTEM,
  COMPOSITION_NOTE_AI_SUMMARY_STARTED_AT_SYSTEM,
  COMPOSITION_NOTE_AI_SUMMARY_SYSTEM,
  COMPOSITION_NOTE_AI_TITLE_SYSTEM,
  COMPOSITION_NOTE_SEARCH_TEXT_SYSTEM,
  ELATION_SANDBOX_ID_SYSTEM,
  ELATION_VISIT_NOTE_TEMPLATE_SYSTEM,
  VISIT_NOTE_BILLING_SECTION_SYSTEM,
  VISIT_NOTE_CONDITION_SECTION_SYSTEM,
  VISIT_NOTE_CONTENT_SECTION_SYSTEM,
  VISIT_NOTE_DOCUMENT_SECTION_SYSTEM,
  VISIT_NOTE_OBSERVATION_SECTION_SYSTEM,
  VISIT_NOTE_ORDER_SECTION_SYSTEM,
} from '#src/codeSystems.ts'
import { Extension } from '#src/decorators.ts'
import {
  hasUnfinishedAiSummaryAttempt,
  isAiSummaryRetryDue,
  isAiSummaryStale,
  nextAiSummaryFailedAttempts,
} from '#src/models/compositionNote.ts'
import { Signable } from '#src/models/signable.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

export const OBSERVATION_SECTION: Coding = {
  system: VISIT_NOTE_OBSERVATION_SECTION_SYSTEM,
  code: 'observation',
  display: 'Observation',
}

/* Coding used to tag the Orders section in a VisitNote Composition. */
export const ORDER_SECTION: Coding = {
  system: VISIT_NOTE_ORDER_SECTION_SYSTEM,
  code: 'orders',
  display: 'Orders',
}

/* Coding used to tag the Conditions section in a VisitNote Composition. */
export const CONDITION_SECTION: Coding = {
  system: VISIT_NOTE_CONDITION_SECTION_SYSTEM,
  code: 'conditions',
  display: 'Conditions',
}

/* Coding used to tag the Documents section in a VisitNote Composition. */
export const DOCUMENT_SECTION: Coding = {
  system: VISIT_NOTE_DOCUMENT_SECTION_SYSTEM,
  code: 'documents',
  display: 'Documents',
}

/* Coding used to tag the Billing section in a VisitNote Composition. */
export const BILLING_SECTION: Coding = {
  system: VISIT_NOTE_BILLING_SECTION_SYSTEM,
  code: 'billing',
  display: 'Billing',
}

export class VisitNote extends Signable<Composition> {
  static readonly RESOURCE_TYPE = 'Composition'

  static isInstance(value: unknown): value is Composition {
    return (
      isResource(value) &&
      isOfType(value, 'Composition') &&
      codeableConceptHasSystem(value.type, ELATION_VISIT_NOTE_TEMPLATE_SYSTEM)
    )
  }

  static queryAll(): QueryTypes {
    return {
      type: `${ELATION_VISIT_NOTE_TEMPLATE_SYSTEM}|`,
      'status:not': 'entered-in-error',
      _sort: '-date',
      _count: '1000',
    }
  }

  static elationVisitNotesQuery({
    patient,
  }: {
    patient?: Reference<Patient>
  }): QueryTypes {
    return {
      ...this.queryAll(),
      patient,
    }
  }

  static byEncounter(encounter: Reference<Encounter>): QueryTypes {
    return {
      ...this.queryAll(),
      encounter,
    }
  }

  static byAppointment(appointment: Reference<Appointment>): QueryTypes {
    return {
      ...this.queryAll(),
      'encounter:Encounter.appointment': appointment,
    }
  }

  // Composition in FHIR R4 has field identifier as a non-array field
  // which does not align with the rest of the FHIR resource specification
  // so we need to handle it separately
  get elationId(): number | undefined {
    const id = this.identifier(ELATION_SANDBOX_ID_SYSTEM)?.value
    return id ? Number(id) : undefined
  }

  set elationId(value: number | undefined) {
    if (value) {
      this.resource.identifier = {
        system: ELATION_SANDBOX_ID_SYSTEM,
        value: value?.toString(),
      }
    } else {
      this.resource.identifier = undefined
    }
  }

  get practitioner(): Reference<Practitioner> | undefined {
    return isOfType(this.resource?.author?.[0], 'Practitioner')
      ? this.resource.author[0]
      : undefined
  }

  get type(): Composition['type'] {
    return this.resource.type
  }

  set type(type: Composition['type']) {
    this.resource.type = type
  }

  get date(): string | undefined {
    return this.resource?.date
  }

  set date(date: string) {
    this.resource.date = date
  }

  get author(): Composition['author'] {
    return this.resource.author
  }

  set author(author: Reference<Practitioner>[]) {
    this.resource.author = author
  }

  get status(): Composition['status'] | undefined {
    return this.resource.status
  }

  set status(status: Composition['status']) {
    this.resource.status = status
  }

  get encounter(): Reference<Encounter> | undefined {
    return this.resource?.encounter
  }

  set encounter(encounter: Reference<Encounter> | undefined) {
    this.resource.encounter = encounter
  }

  get title(): string | undefined {
    return this.aiTitle ?? this.resource?.title
  }

  get displayTitle(): string {
    return this.title ?? 'Untitled'
  }

  set title(title: string) {
    this.resource.title = title
  }

  get reason(): string | undefined {
    const reason = this.resource.section?.find((s) => s.title === 'Reason')
      ?.text?.div

    return reason?.replaceAll(this.reference.reference, '')?.trim()
  }

  get subject(): Reference | undefined {
    return this.resource.subject
  }

  set subject(subject: Reference<Patient>) {
    this.resource.subject = subject
  }

  static isOrderSection(section: CompositionSection): boolean {
    return (
      section.code?.coding?.some(
        (c) => c.system === VISIT_NOTE_ORDER_SECTION_SYSTEM
      ) ?? false
    )
  }

  static isConditionSection(section: CompositionSection): boolean {
    return (
      section.code?.coding?.some(
        (c) => c.system === VISIT_NOTE_CONDITION_SECTION_SYSTEM
      ) ?? false
    )
  }

  static isContentSection(section: CompositionSection): boolean {
    return (
      section.code?.coding?.some(
        (c) => c.system === VISIT_NOTE_CONTENT_SECTION_SYSTEM
      ) ?? false
    )
  }

  static isObservationSection(section: CompositionSection): boolean {
    return (
      section.code?.coding?.some(
        (c) => c.system === VISIT_NOTE_OBSERVATION_SECTION_SYSTEM
      ) ?? false
    )
  }

  static isDocumentSection(section: CompositionSection): boolean {
    return (
      section.code?.coding?.some(
        (c) => c.system === VISIT_NOTE_DOCUMENT_SECTION_SYSTEM
      ) ?? false
    )
  }

  static isBillingSection(section: CompositionSection): boolean {
    return (
      section.code?.coding?.some(
        (c) => c.system === VISIT_NOTE_BILLING_SECTION_SYSTEM
      ) ?? false
    )
  }

  /* All sections except Reason, grouped by title. */
  get sections(): Record<string, CompositionSection[]> {
    const sections =
      this.resource.section?.filter((s) => s.title !== 'Reason') ?? []
    return groupBy(sections, (s) => s.title)
  }

  set sections(sections: Composition['section']) {
    this.resource.section = sections
  }

  get sectionsSummary(): { title: string; text: string }[] {
    return Object.entries(this.sections).flatMap(([title, sections]) =>
      sections.map((s) => ({
        title,
        text: s.text?.div ?? '',
      }))
    )
  }

  /* FHIR's Narrative.div is XHTML by spec, but visit note Content sections
   * store a JSON-serialized Tiptap doc here. */
  get contentSectionDiv(): string | undefined {
    return this.resource.section?.find(VisitNote.isContentSection)?.text?.div
  }

  @Extension({
    url: COMPOSITION_NOTE_AI_SUMMARY_SYSTEM,
    kind: 'valueString',
  })
  aiSummary?: string

  @Extension({
    url: COMPOSITION_NOTE_AI_SUMMARY_GENERATED_AT_SYSTEM,
    kind: 'valueDateTime',
  })
  aiSummaryGeneratedAt?: string

  @Extension({
    url: COMPOSITION_NOTE_AI_SUMMARY_STARTED_AT_SYSTEM,
    kind: 'valueDateTime',
  })
  aiSummaryStartedAt?: string

  @Extension({
    url: COMPOSITION_NOTE_AI_SUMMARY_FAILED_ATTEMPTS_SYSTEM,
    kind: 'valueInteger',
  })
  aiSummaryFailedAttempts?: number

  @Extension({
    url: COMPOSITION_NOTE_AI_TITLE_SYSTEM,
    kind: 'valueString',
  })
  aiTitle?: string

  @Extension({
    url: COMPOSITION_NOTE_SEARCH_TEXT_SYSTEM,
    kind: 'valueString',
  })
  searchText?: string

  get aiSummaryIsStale(): boolean {
    return isAiSummaryStale({
      generatedAt: this.aiSummaryGeneratedAt,
      contentUpdatedAt: this.date,
    })
  }

  get aiSummaryAttemptUnfinished(): boolean {
    return hasUnfinishedAiSummaryAttempt({
      startedAt: this.aiSummaryStartedAt,
      generatedAt: this.aiSummaryGeneratedAt,
      contentUpdatedAt: this.date,
    })
  }

  aiSummaryRetryDue(now?: number): boolean {
    return isAiSummaryRetryDue({
      generatedAt: this.aiSummaryGeneratedAt,
      contentUpdatedAt: this.date,
      failedAttempts: this.aiSummaryFailedAttempts,
      now,
    })
  }

  get nextAiSummaryFailedAttempts(): number {
    return nextAiSummaryFailedAttempts({
      generatedAt: this.aiSummaryGeneratedAt,
      contentUpdatedAt: this.date,
      failedAttempts: this.aiSummaryFailedAttempts,
    })
  }

  protected getExportFields() {
    return {
      id: this.id,
      title: this.title,
      date: this.date,
      reason: this.reason,
      patientId: resolveId(this.subject),
      practitionerId: resolveId(this.practitioner),
      encounterId: resolveId(this.encounter),
      sections: this.sectionsSummary,
    }
  }
}
